import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getFiscalSummary, getBankBalance, updateBankBalance, getStockValue, updateStockValue } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  AreaChart, 
  Area,
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { 
  Download, 
  TrendingUp, 
  TrendingDown,
  Receipt,
  FileText,
  Calendar,
  AlertCircle,
  Loader2,
  Zap,
  Target,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Wallet,
  PiggyBank,
  Landmark,
  Clock3,
  Edit3,
  Check,
  X as XIcon
} from 'lucide-react';
import { toast } from 'sonner';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.4, ease: "easeOut" } 
  }
};

const glowVariants = {
  initial: { opacity: 0.5 },
  animate: { 
    opacity: [0.5, 1, 0.5], 
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" } 
  }
};

const pulseVariants = {
  initial: { scale: 1 },
  animate: { 
    scale: [1, 1.05, 1], 
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" } 
  }
};

const NEON_COLORS = ['#00F0FF', '#39FF14', '#FF0055', '#FFE500', '#BF00FF', '#FF6B00', '#00FF88', '#FF00AA'];

const FiscalPage = () => {
  const [loading, setLoading] = useState(true);
  const [fiscalData, setFiscalData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingAccounting, setExportingAccounting] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [editingBalance, setEditingBalance] = useState(false);
  const [balanceInput, setBalanceInput] = useState('');
  const [editingStock, setEditingStock] = useState(false);
  const [stockInput, setStockInput] = useState('');

  useEffect(() => {
    loadFiscalData();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [selectedYear]);

  const loadFiscalData = async () => {
    setLoading(true);
    try {
      const res = await getFiscalSummary(selectedYear);
      setFiscalData(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données fiscales');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBalance = async () => {
    try {
      const amount = parseFloat(balanceInput.replace(/[^0-9.,\-]/g, '').replace(',', '.'));
      if (isNaN(amount)) { toast.error('Montant invalide'); return; }
      await updateBankBalance({ amount });
      setEditingBalance(false);
      loadFiscalData();
      toast.success('Solde bancaire mis a jour');
    } catch { toast.error('Erreur'); }
  };

  const handleSaveStock = async () => {
    try {
      const amount = parseFloat(stockInput.replace(/[^0-9.,\-]/g, '').replace(',', '.'));
      if (isNaN(amount)) { toast.error('Montant invalide'); return; }
      await updateStockValue({ amount });
      setEditingStock(false);
      loadFiscalData();
      toast.success('Valeur du stock mise a jour');
    } catch { toast.error('Erreur'); }
  };

  const formatCurrency = (amount, currency = 'CHF') => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0) + ' ' + currency;
  };

  const formatCompact = (amount) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k`;
    return amount.toFixed(0);
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/fiscal/export/pdf?year=${selectedYear}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bilan_fiscal_${selectedYear}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Bilan fiscal exporté en PDF');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/fiscal/export/csv?year=${selectedYear}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `donnees_fiscales_${selectedYear}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Données exportées en CSV');
    } catch (error) {
      toast.error('Erreur lors de l\'export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportAccounting = async () => {
    setExportingAccounting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/export/accounting/complete?year=${selectedYear}&format=xlsx`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comptabilite_complete_${selectedYear}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export comptable téléchargé (compatible Bexio/Crésus)');
    } catch (error) {
      toast.error('Erreur lors de l\'export comptable');
    } finally {
      setExportingAccounting(false);
    }
  };

  const years = Array.from({ length: 6 }, (_, i) => (new Date().getFullYear() - i).toString());

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const netProfit = (fiscalData?.total_revenue || 0) - (fiscalData?.total_expenses || 0) + (fiscalData?.stock_value || 0);
  const profitMargin = fiscalData?.total_revenue > 0 
    ? ((netProfit / fiscalData.total_revenue) * 100).toFixed(1) 
    : 0;
  const expenseRatio = fiscalData?.total_revenue > 0 
    ? ((fiscalData.total_expenses / fiscalData.total_revenue) * 100).toFixed(1) 
    : 0;

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1A1A1C] border border-white/10 rounded-sm p-3">
          <p className="text-zinc-400 text-xs mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm font-bold" style={{ color: entry.color }}>
              {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-4 sm:p-6 lg:p-8" data-testid="fiscal-page">
      {/* Background Grid Effect */}
      <div 
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(0,240,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />
      
      {/* Ambient glow effects */}
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-[#00F0FF]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-96 h-96 bg-[#39FF14]/5 rounded-full blur-3xl pointer-events-none" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-[1600px] mx-auto relative z-10"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <motion.div 
                variants={glowVariants}
                initial="initial"
                animate="animate"
                className="w-2 h-2 rounded-full bg-[#39FF14]"
              />
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">
                Analyse fiscale
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Bilan <span className="text-[#00F0FF]">Fiscal</span>
            </h1>
            <p className="text-zinc-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Récapitulatif annuel • Dernière mise à jour {currentTime.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger 
                className="w-[130px] bg-[#111113] border-white/10 text-white hover:border-[#00F0FF]/50 transition-colors"
                data-testid="fiscal-year-select"
              >
                <Calendar className="w-4 h-4 mr-2 text-[#00F0FF]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111113] border-white/10">
                {years.map(year => (
                  <SelectItem key={year} value={year} className="text-white hover:bg-white/5">{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              onClick={handleExportCsv}
              disabled={exportingCsv}
              className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:border-[#39FF14]/50"
              data-testid="export-csv-btn"
            >
              {exportingCsv ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              CSV
            </Button>
            
            <Button 
              variant="outline"
              onClick={handleExportAccounting}
              disabled={exportingAccounting}
              className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:border-[#BF00FF]/50"
              data-testid="export-accounting-btn"
            >
              {exportingAccounting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Comptable
            </Button>
            
            <Button 
              onClick={handleExportPdf}
              disabled={exportingPdf}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold px-6 rounded-sm"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              data-testid="export-pdf-btn"
            >
              {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              EXPORT PDF
            </Button>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.div 
          variants={itemVariants}
          className="bg-[#111113] border border-[#FFE500]/30 rounded-sm p-4 mb-6 flex items-start gap-3"
        >
          <AlertCircle className="w-5 h-5 text-[#FFE500] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-300">
            <span className="text-[#FFE500] font-semibold">Important :</span> Ces données sont fournies à titre indicatif pour faciliter votre déclaration d'impôts. 
            Veuillez consulter votre fiduciaire pour une validation officielle.
          </p>
        </motion.div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Revenue */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#39FF14]/50 transition-colors"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#39FF14]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute top-0 right-0 w-20 h-20 bg-[#39FF14]/10 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Chiffre d'affaires
                </span>
                <TrendingUp className="w-4 h-4 text-[#39FF14]" />
              </div>
              <p className="text-3xl font-bold tracking-tight group-hover:text-[#39FF14] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(fiscalData?.total_revenue)}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-xs text-zinc-500">{fiscalData?.paid_invoices_count || 0} factures payées</span>
              </div>
            </div>
          </motion.div>

          {/* Expenses */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#FF0055]/50 transition-colors"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF0055]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute top-0 right-0 w-20 h-20 bg-[#FF0055]/10 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">Dépenses</span>
                <TrendingDown className="w-4 h-4 text-[#FF0055]" />
              </div>
              <p className="text-3xl font-bold tracking-tight group-hover:text-[#FF0055] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(fiscalData?.total_expenses)}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-xs text-zinc-500">{fiscalData?.expenses_count || 0} dépenses</span>
              </div>
            </div>
          </motion.div>

          {/* Net Profit */}
          <motion.div 
            variants={itemVariants}
            className={`bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#00F0FF]/50 transition-colors ${netProfit < 0 ? 'border-[#FF0055]/30' : ''}`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute top-0 right-0 w-20 h-20 bg-[#00F0FF]/10 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">Bénéfice net</span>
                <PiggyBank className={`w-4 h-4 ${netProfit >= 0 ? 'text-[#00F0FF]' : 'text-[#FF0055]'}`} />
              </div>
              <p className={`text-3xl font-bold tracking-tight transition-colors ${netProfit >= 0 ? 'group-hover:text-[#00F0FF]' : 'text-[#FF0055]'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(netProfit)}
              </p>
              <div className="flex items-center gap-1 mt-2">
                {netProfit >= 0 ? (
                  <ArrowUpRight className="w-3 h-3 text-[#39FF14]" />
                ) : (
                  <ArrowDownRight className="w-3 h-3 text-[#FF0055]" />
                )}
                <span className={`text-xs ${netProfit >= 0 ? 'text-[#39FF14]' : 'text-[#FF0055]'}`}>
                  Marge: {profitMargin}%
                </span>
              </div>
            </div>
          </motion.div>

          {/* VAT */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#BF00FF]/50 transition-colors"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#BF00FF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute top-0 right-0 w-20 h-20 bg-[#BF00FF]/10 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">TVA collectée</span>
                <Receipt className="w-4 h-4 text-[#BF00FF]" />
              </div>
              <p className="text-3xl font-bold tracking-tight group-hover:text-[#BF00FF] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(fiscalData?.total_vat_collected)}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-xs text-zinc-500">À déclarer AFC</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Treasury / Bank Balance Card */}
        <motion.div variants={itemVariants} className="mb-6">
          <div className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FFE500]/3 via-transparent to-[#00F0FF]/3" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-[#FFE500]" />
                  <span className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Tresorerie</span>
                </div>
                {fiscalData?.bank_balance_updated && (
                  <span className="text-xs text-zinc-600 flex items-center gap-1">
                    <Clock3 className="w-3 h-3" />
                    Mis a jour le {new Date(fiscalData.bank_balance_updated).toLocaleDateString('fr-CH')}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Bank Balance */}
                <div className="bg-white/5 rounded-sm p-4">
                  <p className="text-xs text-zinc-500 mb-1">Solde bancaire</p>
                  {editingBalance ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={balanceInput}
                        onChange={(e) => setBalanceInput(e.target.value)}
                        className="h-8 bg-black/50 border-[#FFE500]/30 text-white text-lg"
                        placeholder="0.00"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBalance(); if (e.key === 'Escape') setEditingBalance(false); }}
                        data-testid="bank-balance-input"
                      />
                      <button onClick={handleSaveBalance} className="p-1 text-[#39FF14] hover:bg-white/10 rounded"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingBalance(false)} className="p-1 text-[#FF0055] hover:bg-white/10 rounded"><XIcon className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-bold text-[#FFE500]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {fiscalData?.bank_balance != null ? formatCurrency(fiscalData.bank_balance) : '- -'}
                      </p>
                      <button
                        onClick={() => { setBalanceInput(fiscalData?.bank_balance?.toString() || '0'); setEditingBalance(true); }}
                        className="p-1 text-zinc-600 hover:text-[#FFE500] hover:bg-white/10 rounded transition-colors"
                        data-testid="edit-bank-balance-btn"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Pending Revenue */}
                <div className="bg-white/5 rounded-sm p-4">
                  <p className="text-xs text-zinc-500 mb-1">Factures en attente</p>
                  <p className="text-2xl font-bold text-[#00F0FF]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    +{formatCurrency(fiscalData?.pending_revenue || 0)}
                  </p>
                  <p className="text-xs text-zinc-600 mt-1">A encaisser</p>
                </div>

                {/* Net this year */}
                <div className="bg-white/5 rounded-sm p-4">
                  <p className="text-xs text-zinc-500 mb-1">Benefice net {selectedYear}</p>
                  <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-[#39FF14]' : 'text-[#FF0055]'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {netProfit >= 0 ? '+' : ''}{formatCurrency(netProfit)}
                  </p>
                  <p className="text-xs text-zinc-600 mt-1">Revenus - Depenses</p>
                </div>

                {/* Forecast */}
                <div className="bg-white/5 rounded-sm p-4 border border-[#FFE500]/20">
                  <p className="text-xs text-zinc-500 mb-1">Tresorerie previsionnelle</p>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {fiscalData?.forecast_treasury != null ? formatCurrency(fiscalData.forecast_treasury) : '- -'}
                  </p>
                  <p className="text-xs text-zinc-600 mt-1">Solde + en attente</p>
                </div>

                {/* Stock Value */}
                <div className="bg-white/5 rounded-sm p-4 border border-white/10">
                  <p className="text-xs text-zinc-500 mb-1">Valeur du stock (inventaire)</p>
                  {editingStock ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={stockInput}
                        onChange={(e) => setStockInput(e.target.value)}
                        className="bg-black border border-white/20 text-white px-2 py-1 rounded text-lg w-full font-mono"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveStock(); if (e.key === 'Escape') setEditingStock(false); }}
                        data-testid="stock-value-input"
                      />
                      <button onClick={handleSaveStock} className="p-1 text-[#39FF14] hover:bg-white/10 rounded"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingStock(false)} className="p-1 text-[#FF0055] hover:bg-white/10 rounded"><XIcon className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {fiscalData?.stock_value != null ? formatCurrency(fiscalData.stock_value) : '- -'}
                      </p>
                      <button
                        onClick={() => { setStockInput(fiscalData?.stock_value?.toString() || '0'); setEditingStock(true); }}
                        className="p-1 text-zinc-500 hover:text-white hover:bg-white/10 rounded transition-colors"
                        data-testid="edit-stock-value-btn"
                      ><Edit3 className="w-3 h-3" /></button>
                    </div>
                  )}
                  {fiscalData?.stock_value_updated && (
                    <p className="text-xs text-zinc-600 mt-1">Mis a jour le {new Date(fiscalData.stock_value_updated).toLocaleDateString('fr-CH')}</p>
                  )}
                  {!fiscalData?.stock_value_updated && <p className="text-xs text-zinc-600 mt-1">Valeur au 31.12 pour la declaration</p>}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* KPI Indicators Row */}
        <motion.div variants={itemVariants} className="grid grid-cols-3 gap-4 mb-6">
          {/* Expense Ratio */}
          <div className="bg-[#111113] border border-white/10 rounded-sm p-4 flex items-center gap-4">
            <motion.div 
              variants={pulseVariants}
              initial="initial"
              animate="animate"
              className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#FF0055]/20 to-[#FF0055]/5 flex items-center justify-center"
            >
              <Percent className="w-5 h-5 text-[#FF0055]" />
            </motion.div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {expenseRatio}<span className="text-sm text-zinc-500">%</span>
              </p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Ratio dépenses</p>
            </div>
          </div>

          {/* Average Invoice */}
          <div className="bg-[#111113] border border-white/10 rounded-sm p-4 flex items-center gap-4">
            <motion.div 
              variants={pulseVariants}
              initial="initial"
              animate="animate"
              className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 flex items-center justify-center"
            >
              <Target className="w-5 h-5 text-[#00F0FF]" />
            </motion.div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCompact(fiscalData?.paid_invoices_count > 0 ? fiscalData.total_revenue / fiscalData.paid_invoices_count : 0)}
              </p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Facture moyenne</p>
            </div>
          </div>

          {/* Monthly Average */}
          <div className="bg-[#111113] border border-white/10 rounded-sm p-4 flex items-center gap-4">
            <motion.div 
              variants={pulseVariants}
              initial="initial"
              animate="animate"
              className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#39FF14]/20 to-[#39FF14]/5 flex items-center justify-center"
            >
              <Activity className="w-5 h-5 text-[#39FF14]" />
            </motion.div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCompact(fiscalData?.total_revenue / 12 || 0)}
              </p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">CA mensuel moy.</p>
            </div>
          </div>
        </motion.div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Quarterly Revenue */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Revenus par <span className="text-[#00F0FF]">Trimestre</span>
                </h3>
                <p className="text-xs text-zinc-500 mt-1">Chiffre d'affaires encaissé</p>
              </div>
              <div className="w-8 h-8 rounded-sm bg-[#00F0FF]/10 flex items-center justify-center">
                <Zap className="w-4 h-4 text-[#00F0FF]" />
              </div>
            </div>
            {fiscalData?.quarterly_revenue && fiscalData.quarterly_revenue.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fiscalData.quarterly_revenue}>
                    <XAxis 
                      dataKey="quarter" 
                      tick={{ fill: '#71717A', fontSize: 11 }} 
                      axisLine={false} 
                      tickLine={false} 
                    />
                    <YAxis 
                      tick={{ fill: '#71717A', fontSize: 11 }} 
                      axisLine={false} 
                      tickLine={false} 
                      tickFormatter={(v) => `${formatCompact(v)}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="amount" 
                      fill="url(#barGradient)" 
                      radius={[4, 4, 0, 0]}
                    />
                    <defs>
                      <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00F0FF" />
                        <stop offset="100%" stopColor="#00F0FF" stopOpacity={0.3} />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-zinc-500">
                <p>Aucune donnée pour {selectedYear}</p>
              </div>
            )}
          </motion.div>

          {/* Expenses by Category */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Répartition des <span className="text-[#FF0055]">Dépenses</span>
                </h3>
                <p className="text-xs text-zinc-500 mt-1">Par catégorie</p>
              </div>
              <div className="w-8 h-8 rounded-sm bg-[#FF0055]/10 flex items-center justify-center">
                <Wallet className="w-4 h-4 text-[#FF0055]" />
              </div>
            </div>
            {fiscalData?.expenses_by_category && fiscalData.expenses_by_category.length > 0 ? (
              <div className="h-[250px] flex items-center">
                <ResponsiveContainer width="50%" height="100%">
                  <PieChart>
                    <Pie
                      data={fiscalData.expenses_by_category}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={55}
                      strokeWidth={0}
                    >
                      {fiscalData.expenses_by_category.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={NEON_COLORS[index % NEON_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="w-[50%] space-y-2 pl-4">
                  {fiscalData.expenses_by_category.slice(0, 6).map((cat, index) => (
                    <div key={cat.category} className="flex items-center gap-2 text-sm group">
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: NEON_COLORS[index % NEON_COLORS.length] }}
                      />
                      <span className="text-zinc-400 truncate flex-1 group-hover:text-white transition-colors">
                        {cat.category_name}
                      </span>
                      <span 
                        className="font-mono text-xs font-medium"
                        style={{ color: NEON_COLORS[index % NEON_COLORS.length] }}
                      >
                        {formatCompact(cat.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-zinc-500">
                <p>Aucune dépense enregistrée</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Monthly Evolution Chart */}
        <motion.div 
          variants={itemVariants}
          className="bg-[#111113] border border-white/10 rounded-sm p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Évolution <span className="text-[#39FF14]">Mensuelle</span>
              </h3>
              <p className="text-xs text-zinc-500 mt-1">Comparaison revenus vs dépenses</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-1 bg-[#39FF14] rounded-full" />
                <span className="text-zinc-400">Revenus</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-1 bg-[#FF0055] rounded-full" />
                <span className="text-zinc-400">Dépenses</span>
              </div>
            </div>
          </div>
          {fiscalData?.monthly_data && fiscalData.monthly_data.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={fiscalData.monthly_data}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#39FF14" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#39FF14" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF0055" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#FF0055" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#71717A', fontSize: 11 }} 
                    axisLine={false} 
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fill: '#71717A', fontSize: 11 }} 
                    axisLine={false} 
                    tickLine={false}
                    tickFormatter={(v) => `${formatCompact(v)}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#39FF14" 
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="expenses" 
                    stroke="#FF0055" 
                    strokeWidth={2}
                    fill="url(#expenseGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-zinc-500">
              <p>Aucune donnée mensuelle disponible</p>
            </div>
          )}
        </motion.div>

        {/* Bottom Summary */}
        <motion.div variants={itemVariants} className="mt-6 text-center">
          <p className="text-xs text-zinc-600 uppercase tracking-[0.2em]">
            Xyron Core ERP • Bilan Fiscal {selectedYear}
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default FiscalPage;
